import { COLORS } from '@btime/constants'
import { capitalizeIfLower, formatAddressToObj, parseBoolean } from '@btime/helpers'
import { is, prop, type } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Text } from '../../components/presentation/Text'
import { hasValue } from '../../utils/helpers/value'
import ActionPlansList from '../actionPlans/ActionPlansList'
import DateTimeIcons from '../../components/presentation/DateTimeIcons'
import DynamicBlockPresentation from './block/DynamicBlockPresentation'
import Gallery from '../../components/presentation/gallery/Gallery'
import Markdown from '../../components/presentation/Markdown'
import PlacesList from '../places/PlacesList'
import Separator from '../../components/helpers/Separator'
import Signature from '../../components/presentation/Signature'
import Tag, { Tags } from '../../components/presentation/Tag'
import Stock from '../../components/presentation/Stock'
import CoordinatesMap from '../../components/map/CoordinatesMap'

const renderValue = (value, valueType, props, t) => {
  if (value === null) {
    return <Tag label={t('common:notAvailable')} bgColor={COLORS.dark3} medium />
  }

  switch (valueType) {
    case 'compliance':
    case 'checkbox':
      const active = parseBoolean(value)
      const { labels } = props || {}
      return (
        <Tag
          label={active ? labels?.true || t('common:yes') : labels?.false || t('common:no')}
          bgColor={active ? COLORS.green : COLORS.red}
          medium
        />
      )

    case 'multiple':
      return <Tags values={value.map(prop('name'))} medium />

    case 'pending':
      const pendencyName = capitalizeIfLower(value?.name)
      const pendencyDescription = capitalizeIfLower(value?.description)

      return (
        <div>
          <Tag label={pendencyName} bgColor={COLORS.purple} medium />
          <Separator weight={2} />
          <Text preLine>{pendencyDescription}</Text>
        </div>
      )

    case 'datetime':
      return <DateTimeIcons gutter={0.4} date={value} />

    case 'date':
      return <DateTimeIcons gutter={0.4} hideTime date={value.split('T')[0]} />

    case 'select':
      return <Tag label={value?.name ?? value} bgColor={COLORS.dark3} medium />

    case 'system_status':
      return (
        <Tag
          label={value ? t('common:active') : t('common:inactive')}
          bgColor={value ? COLORS.green : COLORS.red}
          medium
        />
      )

    case 'sign':
    case 'signature':
      return <Signature item={value} />

    case 'gallery':
      return <Gallery items={value} />

    case 'address':
    case 'location':
      value = formatAddressToObj(value)
      const address = prop('search', value)
      const complement = prop('complement', value)
      return <Text>{`${address}${complement ? ` -  ${complement}` : ''}`}</Text>

    case 'place_location':
    case 'place_person':
      return <PlacesList items={value} />

    case 'action_plan':
      return <ActionPlansList items={value} />

    case 'informative':
      return false

    case 'block':
      return <DynamicBlockPresentation value={value} />

    case 'place':
    case 'user':
    case 'asset':
      let label = ''
      if (is(Array, value)) {
        label = value.map(prop('name')).join(', ')
      } else {
        label = value.name
      }
      return <Text>{label}</Text>

    case 'text':
      return <Markdown>{value || ''}</Markdown>

    case 'stock':
      return <Stock value={value} />

    case 'integration':
      return <Text preLine>{value.name}</Text>

    case 'coordinates':
      return <CoordinatesMap value={value} />

    default:
      const dataType = type(value)
      if (dataType === 'Object' || dataType === 'Array') return <Text>**{t('common:fieldInDevelopment')}**</Text>
      return <Text preLine>{(hasValue(value) ? value : '').toString()}</Text>
  }
}

const DynamicPresentation = ({ value, type, ...props }) => {
  const { t } = useTranslation('common')
  if (value === undefined) return false
  return renderValue(value, type, props || {}, t)
}

export default DynamicPresentation
